// src/hooks/useEnvironmentFilter.tsx
// Shared environment filter state for Dashboard, Export, and NL Query.
//
// Provides:
//   - allEnvironments: full list fetched from GET /environments/
//   - selectedIds: currently selected environment IDs
//   - isFiltered: true when the user has narrowed below "all"
//   - toggle / selectAll / setSelection mutations
//
// Persistence: sessionStorage (survives navigation, not browser close).
// Hidden when user has <= 1 environment (nothing to filter).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchData } from "api/apiHelpers";
import logger from "utils/logger";
import { Environment } from "types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EnvironmentFilterContextValue {
  /** All environments the user has access to */
  allEnvironments: Environment[];
  /** Currently selected environment IDs */
  selectedIds: number[];
  /** True when selectedIds is a proper subset of all environments */
  isFiltered: boolean;
  /** True when there are 2+ environments (show the filter) */
  showFilter: boolean;
  /** Loading state for the initial fetch */
  loading: boolean;
  /** Toggle a single environment in/out of the selection */
  toggle: (id: number) => void;
  /** Reset selection to all environments */
  selectAll: () => void;
  /** Explicitly set the selected IDs */
  setSelection: (ids: number[]) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const EnvironmentFilterContext =
  createContext<EnvironmentFilterContextValue | null>(null);

export function useEnvironmentFilter(): EnvironmentFilterContextValue {
  const ctx = useContext(EnvironmentFilterContext);
  if (!ctx) {
    // Safe fallback outside provider — no filtering
    return {
      allEnvironments: [],
      selectedIds: [],
      isFiltered: false,
      showFilter: false,
      loading: false,
      toggle: () => {},
      selectAll: () => {},
      setSelection: () => {},
    };
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// SessionStorage helpers
// ---------------------------------------------------------------------------

const STORAGE_KEY = "athena_env_filter";

// 2026-06-10 (cursor_logs/042): when false, the dashboard filter always opens
// with all environments selected (the saved selection is NOT restored on load).
// Flip to true to restore the previous "remember my filter within the session"
// behavior — the persistence machinery below is kept intact for exactly that.
const RESTORE_SAVED_FILTER_ON_LOAD = false;

function loadFromSession(): number[] | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((v: unknown) => typeof v === "number")) {
      return parsed;
    }
  } catch {
    // Corrupted — ignore
  }
  return null;
}

function saveToSession(ids: number[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Quota exceeded or incognito — ignore
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const EnvironmentFilterProvider: React.FC<{
  /** Pre-fetched environments (avoids double-fetch if dashboard already has them) */
  environments?: Environment[];
  children: React.ReactNode;
}> = ({ environments: envProp, children }) => {
  const [allEnvironments, setAllEnvironments] = useState<Environment[]>(
    envProp ?? []
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(!envProp);

  // Fetch environments if not provided via prop
  useEffect(() => {
    if (envProp) {
      setAllEnvironments(envProp);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const envs = await fetchData<Environment[]>("/environments/");
        setAllEnvironments(envs);
      } catch (err) {
        logger.error("Failed to fetch environments for filter:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [envProp]);

  // Initialise selected IDs once environments are loaded.
  //
  // 2026-06-10 (cursor_logs/042): the dashboard filter now ALWAYS opens with
  // every environment selected. Joan found it confusing that the dashboard
  // could open pre-filtered from a stale saved selection. The sessionStorage
  // persistence is intentionally KEPT (saveToSession on change + loadFromSession
  // helper) so within-session changes still survive re-renders and the
  // restore-on-load behavior can be re-enabled by flipping the flag below.
  useEffect(() => {
    if (allEnvironments.length === 0) return;
    const allIds = allEnvironments.map((e) => Number(e.id));
    if (RESTORE_SAVED_FILTER_ON_LOAD) {
      const saved = loadFromSession();
      if (saved && saved.length > 0) {
        // Only keep saved IDs that still exist in the current env list
        const valid = saved.filter((id) => allIds.includes(id));
        setSelectedIds(valid.length > 0 ? valid : allIds);
        return;
      }
    }
    setSelectedIds(allIds);
  }, [allEnvironments]);

  // Persist to sessionStorage on change
  useEffect(() => {
    if (selectedIds.length > 0) {
      saveToSession(selectedIds);
    }
  }, [selectedIds]);

  const allIds = useMemo(
    () => allEnvironments.map((e) => Number(e.id)),
    [allEnvironments]
  );

  const isFiltered = useMemo(() => {
    if (allIds.length === 0) return false;
    if (selectedIds.length === allIds.length) return false;
    return true;
  }, [allIds, selectedIds]);

  const showFilter = allEnvironments.length > 1;

  const toggle = useCallback(
    (id: number) => {
      setSelectedIds((prev) => {
        const next = prev.includes(id)
          ? prev.filter((eid) => eid !== id)
          : [...prev, id];
        // Prevent empty selection — auto-reset to all
        return next.length === 0 ? allIds : next;
      });
    },
    [allIds]
  );

  const selectAll = useCallback(() => {
    setSelectedIds(allIds);
  }, [allIds]);

  const setSelection = useCallback(
    (ids: number[]) => {
      const valid = ids.filter((id) => allIds.includes(id));
      setSelectedIds(valid.length > 0 ? valid : allIds);
    },
    [allIds]
  );

  const value = useMemo<EnvironmentFilterContextValue>(
    () => ({
      allEnvironments,
      selectedIds,
      isFiltered,
      showFilter,
      loading,
      toggle,
      selectAll,
      setSelection,
    }),
    [
      allEnvironments,
      selectedIds,
      isFiltered,
      showFilter,
      loading,
      toggle,
      selectAll,
      setSelection,
    ]
  );

  return (
    <EnvironmentFilterContext.Provider value={value}>
      {children}
    </EnvironmentFilterContext.Provider>
  );
};

export default useEnvironmentFilter;
